class SeoSectionQuickLinks {
  constructor() {
    this.sections = document.querySelectorAll('.seo-section');
    this.init();
  }

  init() {
    // Process each section on the page
    this.sections.forEach(section => {
      this.processSection(section);
    });

    // Set up IntersectionObserver for lazy initialization failsafe
    this.setupIntersectionObserver();

    // Set up MutationObserver for dynamic content changes
    this.setupMutationObserver();
  }

  processSection(section) {
    const sidebar = section.querySelector('.seo-section__sidebar');
    const main = section.querySelector('.seo-section__main');
    const linksContainer = section.querySelector('[data-seo-sidebar-links]');

    if (!sidebar || !main || !linksContainer) return;

    // First, add classes to any h3 and h4 tags that don't have them
    const h3Tags = main.querySelectorAll('h3:not(.seo-section__heading)');
    const h4Tags = main.querySelectorAll('h4:not(.seo-section__subheading)');
    
    h3Tags.forEach(h3 => {
      h3.classList.add('seo-section__heading');
    });
    
    h4Tags.forEach(h4 => {
      h4.classList.add('seo-section__subheading');
    });

    // Find all headings in the main content
    const headings = main.querySelectorAll('.seo-section__heading, .seo-section__subheading');
    
    if (headings.length === 0) {
      // Hide sidebar if no headings found
      sidebar.style.display = 'none';
      return;
    }

    // Clear existing links
    linksContainer.innerHTML = '';

    // Generate unique IDs and create links
    const usedIds = new Set();
    const links = [];

    headings.forEach((heading, index) => {
      // Skip empty headings
      const text = heading.textContent.trim();
      if (!text) return;

      // Generate unique ID
      let id = this.slugify(text);
      let counter = 1;
      while (usedIds.has(id)) {
        id = `${this.slugify(text)}-${counter}`;
        counter++;
      }
      usedIds.add(id);

      // Set ID on heading
      heading.id = id;

      // Create link data
      links.push({
        id,
        text,
        level: heading.classList.contains('seo-section__heading') ? 'h3' : 'h4',
        element: heading
      });
    });

    // Build the links HTML
    links.forEach((link, index) => {
      const linkElement = document.createElement('a');
      linkElement.href = `#${link.id}`;
      linkElement.className = 'seo-section__sidebar-link';
      linkElement.textContent = link.text;
      
      // Add indentation for h4 elements
      if (link.level === 'h4') {
        // linkElement.style.paddingLeft = '20px';
      }

      // Add smooth scrolling behavior
      linkElement.addEventListener('click', (e) => {
        e.preventDefault();
        this.smoothScrollTo(link.element);
      });

      linksContainer.appendChild(linkElement);

      // Add divider after each link except the last
      if (index < links.length - 1) {
        const divider = document.createElement('hr');
        divider.className = 'seo-section__sidebar-divider';
        linksContainer.appendChild(divider);
      }
    });

    // Set up scroll spy for active link highlighting
    this.setupScrollSpy(section, links);
  }

  slugify(text) {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim();
  }

  smoothScrollTo(element) {
    // Get header height for offset
    const header = document.querySelector('.shopify-section-header');
    const headerHeight = header ? header.offsetHeight : 0;
    const offset = 200; // 200px offset as requested

    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - headerHeight - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  setupScrollSpy(section, links) {
    const linksContainer = section.querySelector('[data-seo-sidebar-links]');
    const linkElements = linksContainer.querySelectorAll('a');
    
    const observerOptions = {
      rootMargin: '-20% 0px -70% 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Remove active class from all links
          linkElements.forEach(link => link.classList.remove('seo-section__sidebar-link--active'));
          
          // Add active class to corresponding link
          const activeLink = linksContainer.querySelector(`a[href="#${entry.target.id}"]`);
          if (activeLink) {
            activeLink.classList.add('seo-section__sidebar-link--active');
          }
        }
      });
    }, observerOptions);

    // Observe all heading elements
    links.forEach(link => {
      observer.observe(link.element);
    });
  }

  setupIntersectionObserver() {
    // Failsafe: Re-process sections when they come into view
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const linksContainer = entry.target.querySelector('[data-seo-sidebar-links]');
          // Only process if links haven't been generated yet
          if (linksContainer && linksContainer.children.length === 0) {
            this.processSection(entry.target);
          }
        }
      });
    });

    this.sections.forEach(section => {
      observer.observe(section);
    });
  }

  setupMutationObserver() {
    // Watch for dynamic content changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          // Check if the mutation happened within a seo-section
          const section = mutation.target.closest('.seo-section');
          if (section) {
            // Debounce to avoid multiple rapid updates
            clearTimeout(this.mutationTimeout);
            this.mutationTimeout = setTimeout(() => {
              this.processSection(section);
            }, 100);
          }
        }
      });
    });

    // Observe all main content areas
    this.sections.forEach(section => {
      const main = section.querySelector('.seo-section__main');
      if (main) {
        observer.observe(main, { childList: true, subtree: true });
      }
    });
  }
}

// Add CSS for active link styling
const style = document.createElement('style');
style.textContent = `
  .seo-section__sidebar-link--active {
    font-weight: 600;
    text-decoration: underline;
  }
`;
document.head.appendChild(style);

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  new SeoSectionQuickLinks();
});